import csrfToken from '../csrfToken.js';

export function checkStatus(response) {
  if (response.status >= 200 && response.status < 300) {
    return response;
  } else {
    var error = new Error(response.statusText);
    error.response = response;
    throw error;
  }
}

export function parseJSON(response) {
  return response.json();
}

// Parse the JSON body of a failed request. checkStatus attaches the raw
// Response as error.response; this reads it once and resolves to the parsed
// object so a caller can pull out whatever it needs (base errors,
// field-specific errors, etc.) from that single read. Resolves to null when
// there is no response (e.g. a network failure, where fetch rejects before
// checkStatus runs) or the body isn't JSON.
export function errorBody(error) {
  const response = error && error.response;
  if (!response || typeof response.json !== 'function') {
    return Promise.resolve(null);
  }
  return response.json().catch(() => null);
}

// Pull Rails' conventional `base` errors (those not tied to a specific field)
// out of an already-parsed response body, joined into one string. Returns null
// when there is no base error. Pair with errorBody, e.g.
// errorBody(error).then(body => baseErrorMessage(body)).
export function baseErrorMessage(body) {
  const base = body && body.errors && body.errors.base;
  if (Array.isArray(base) && base.length > 0) {
    return base.join(' ');
  }
  return null;
}

export function deleteJSON(url) {
  if (!url) {
    throw new Error('invalid url');
  }
  const token = csrfToken();
  return fetch(url, {
    method: 'DELETE',
    headers: {
      'Accept': 'application/json',
      'X-CSRF-Token': token,
      'X-HTTP-Method-Override': 'DELETE',
      'X-Requested-With': 'XMLHttpRequest',
    },
    credentials: 'same-origin',
  }).then(checkStatus).then(parseJSON);
}

export function del(url) {
  if (!url) {
    throw new Error('invalid url');
  }
  const token = csrfToken();
  return fetch(url, {
    method: 'DELETE',
    headers: {
      'X-CSRF-Token': token,
      'X-HTTP-Method-Override': 'DELETE',
      'X-Requested-With': 'XMLHttpRequest',
    },
    credentials: 'same-origin',
  }).then(checkStatus);
}

export function getJSON(url) {
  if (!url) {
    throw new Error('invalid url');
  }
  return fetch(url, {
    headers: {
      'Accept': 'application/json',
      'Cache-Control': 'no-cache, max-age=0',
      'X-Requested-With': 'XMLHttpRequest',
    },
    credentials: 'same-origin',
  }).then(checkStatus).then(parseJSON);
}

export function get(url) {
  if (!url) {
    throw new Error('invalid url');
  }
  return fetch(url, {
    headers: {
      'Cache-Control': 'no-cache, max-age=0',
      'X-Requested-With': 'XMLHttpRequest',
    },
    credentials: 'same-origin',
  }).then(checkStatus);
}

export function patchFormData(url, data) {
  if (!url) {
    throw new Error('invalid url');
  }
  return fetch(url, {
    method: 'PATCH',
    headers: {
      'Accept': 'application/json',
      'X-CSRF-Token': csrfToken(),
      'X-Requested-With': 'XMLHttpRequest',
    },
    credentials: 'same-origin',
    body: data,
  }).then(checkStatus).then(parseJSON);
}

export function patchJSON(url, params) {
  if (!url) {
    throw new Error('invalid url');
  }
  const token = csrfToken();
  if (!params) {
    params = {};
  }
  return fetch(url, {
    method: 'PATCH',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-Token': token,
      'X-HTTP-Method-Override': 'PATCH',
      'X-Requested-With': 'XMLHttpRequest',
    },
    credentials: 'same-origin',
    body: JSON.stringify(params),
  }).then(checkStatus).then(parseJSON);
}

export function post(url, params = {}) {
  if (!url) {
    throw new Error('invalid url');
  }
  const token = csrfToken();
  if (!params) {
    params = {};
  }
  return fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': token,
      'X-Requested-With': 'XMLHttpRequest',
    },
    credentials: 'same-origin',
    body: JSON.stringify(params),
  }).then(checkStatus);
}

export function postFormData(url, data) {
  if (!url) {
    throw new Error('invalid url');
  }
  return fetch(url, {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'X-CSRF-Token': csrfToken(),
      'X-Requested-With': 'XMLHttpRequest',
    },
    credentials: 'same-origin',
    body: data,
  }).then(checkStatus).then(parseJSON);
}

export function postJSON(url, params = {}) {
  if (!url) {
    throw new Error('invalid url');
  }
  const token = csrfToken();
  if (!params) {
    params = {};
  }
  return fetch(url, {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-Token': token,
      'X-Requested-With': 'XMLHttpRequest',
    },
    credentials: 'same-origin',
    body: JSON.stringify(params),
  }).then(checkStatus).then(parseJSON);
}

export function putJSON(url, params) {
  if (!url) {
    throw new Error('invalid url');
  }
  const token = csrfToken();
  if (!params) {
    params = {};
  }
  return fetch(url, {
    method: 'PUT',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-Token': token,
      'X-HTTP-Method-Override': 'PUT',
      'X-Requested-With': 'XMLHttpRequest',
    },
    credentials: 'same-origin',
    body: JSON.stringify(params),
  }).then(checkStatus).then(parseJSON);
}

export function getWithAuth(url, params, auth) {
  if (!url) {
    throw new Error('invalid url');
  }
  const token = csrfToken();
  if (!params) {
    params = {};
  }
  let authStr = '';
  if (!authorization) {
    auth = ''
  } else if (authorization.type !== undefined && authorization.type.trim() !== '') {
    authStr = toString(authorization.type) + toString(authorization.token);
  } else {
    authStr = toString(authorization.token);
  }
  return fetch(url, {
    method: 'PUT',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-Token': token,
      'X-HTTP-Method-Override': 'PUT',
      'X-Requested-With': 'XMLHttpRequest',
      'Authorization': authStr,
    },
    credentials: 'same-origin',
    body: JSON.stringify(params),
  }).then(checkStatus)
}

// XHR-based upload with progress events. Returns {xhr, promise}: hold onto
// xhr to call .abort(); await promise to get the parsed JSON response or an
// error. options.onProgress(fraction) is called with a 0..1 fraction as the
// upload progresses. options.fieldName defaults to "file".
export function xhrUpload(url, file, options = {}) {
  if (!url) {
    throw new Error('invalid url');
  }
  const fieldName = options.fieldName || 'file';
  const xhr = new XMLHttpRequest();
  const promise = new Promise((resolve, reject) => {
    xhr.open('POST', url, true);
    xhr.responseType = 'json';
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.setRequestHeader('X-CSRF-Token', csrfToken());
    xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
    xhr.withCredentials = true;
    if (options.onProgress && xhr.upload) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          options.onProgress(event.loaded / event.total);
        }
      });
    }
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr.response);
      } else {
        const error = new Error(xhr.statusText || 'upload failed');
        error.status = xhr.status;
        error.response = xhr.response;
        reject(error);
      }
    });
    xhr.addEventListener('error', () => reject(new Error('network error')));
    xhr.addEventListener('abort', () => {
      const err = new Error('aborted');
      err.aborted = true;
      reject(err);
    });
    const formData = new FormData();
    formData.append(fieldName, file);
    xhr.send(formData);
  });
  return {xhr, promise};
}
